// Store references to our event handlers so we can remove them.
/** @type {Record<string, (event: Event) => void>} */
const trapFocusHandlers = {};

/** @type {HTMLElement | null} */
let trappedContainer = null;

/**
 * Get all focusable elements within a container.
 * @param {HTMLElement} container - The container to get focusable elements from.
 * @returns {HTMLElement[]} An array of focusable elements.
 */
function getFocusableElements(container) {
  return Array.from(
    container.querySelectorAll(
      "summary, a[href], button:enabled, [tabindex]:not([tabindex^='-']), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe"
    )
  ).filter((el) => {
    if (!(el instanceof HTMLElement)) return false;
    if (el.hasAttribute('disabled') || el.getAttribute('aria-hidden') === 'true') return false;
    // offsetParent is null for fixed/sticky in some cases; also accept visibility via client rects
    const style = window.getComputedStyle(el);
    if (style.visibility === 'hidden' || style.display === 'none') return false;
    return true;
  });
}

/**
 * Trap focus within the given container.
 * @param {HTMLElement} container - The container to trap focus within.
 * @param {{ initialFocus?: HTMLElement | null }} [options]
 */
export function trapFocus(container, options = {}) {
  // Clean up any previously set traps.
  removeTrapFocus();

  trappedContainer = container;

  // Gather focusable elements.
  const focusable = getFocusableElements(container);
  const initial =
    options.initialFocus && container.contains(options.initialFocus)
      ? options.initialFocus
      : focusable[0] || container;

  if (container instanceof HTMLElement && !container.hasAttribute('tabindex')) {
    container.setAttribute('tabindex', '-1');
  }

  // Keydown handler for cycling focus with Tab and Shift+Tab
  /** @type {(event: KeyboardEvent) => void} */
  trapFocusHandlers.keydown = (event) => {
    if (event.key !== 'Tab') return;

    const activeEl = document.activeElement;
    const items = getFocusableElements(container);

    if (!items.length) {
      event.preventDefault();
      container.focus({ preventScroll: true });
      return;
    }

    const first = items[0];
    const last = items[items.length - 1];

    // If on the last focusable and tabbing forward, go to first
    if (!event.shiftKey && activeEl === last) {
      event.preventDefault();
      first?.focus({ preventScroll: true });
    }
    // If on the first (or the container) and shift-tabbing, go to last
    else if (event.shiftKey && (activeEl === first || activeEl === container)) {
      event.preventDefault();
      last?.focus({ preventScroll: true });
    }
  };

  // Focusin (capturing) handler to forcibly keep focus in the container
  /** @type {(event: FocusEvent) => void} */
  trapFocusHandlers.focusin = (event) => {
    // If the newly focused element isn't inside the container, redirect focus back.
    if (event.target instanceof Node && !container.contains(event.target)) {
      event.stopPropagation();
      const items = getFocusableElements(container);
      (items[0] || container)?.focus({ preventScroll: true });
    }
  };

  // Attach the handlers
  document.addEventListener('keydown', trapFocusHandlers.keydown, true);
  // Use capture phase for focusin so we can catch it before it lands outside
  document.addEventListener('focusin', trapFocusHandlers.focusin, true);

  // Finally, put focus where you want it.
  requestAnimationFrame(() => {
    initial?.focus({ preventScroll: true });
  });
}

/**
 * Remove focus trap and optionally refocus another element.
 * @param {HTMLElement | null} [elementToFocus]
 */
export function removeTrapFocus(elementToFocus = null) {
  trapFocusHandlers.keydown && document.removeEventListener('keydown', trapFocusHandlers.keydown, true);
  trapFocusHandlers.focusin && document.removeEventListener('focusin', trapFocusHandlers.focusin, true);
  delete trapFocusHandlers.keydown;
  delete trapFocusHandlers.focusin;
  trappedContainer = null;

  if (elementToFocus instanceof HTMLElement) {
    // Defer so callers that unlock body scroll (and may blur) run first.
    requestAnimationFrame(() => {
      try {
        elementToFocus.focus({ preventScroll: true });
      } catch (_) {
        // Element may have been removed from the document.
      }
    });
  }
}

/**
 * Cycle focus to the next or previous link
 *
 * @param {HTMLElement[]} items
 * @param {number} increment
 */
export function cycleFocus(items, increment) {
  const currentIndex = items.findIndex((item) => item.matches(':focus'));
  let targetIndex = currentIndex + increment;

  if (targetIndex >= items.length) {
    targetIndex = 0;
  } else if (targetIndex < 0) {
    targetIndex = items.length - 1;
  }

  const targetItem = items[targetIndex];

  if (!targetItem) return;

  targetItem.focus();
}
